/**
 * OnbEmailOtp — onboarding step 3 (6-digit email code).
 *
 * Verifies the signup OTP via `useEmailVerify` (`POST /auth/email/verify`, body
 * `{user_id, otp}`). On a correct code the endpoint issues a session and the
 * flow stores the returned `access_token`. Six single-digit inputs with
 * auto-advance / backspace-back, plus a resend countdown that re-issues through
 * the flow's `onResend`.
 *
 * NON-PROD demo: when `devOtp` is present (QA/local, no real email delivery)
 * the code is shown on-screen so a tester can complete verification.
 *
 * 2026-08: the code boxes are the instrument face (`.g-data`) — they are
 * literally six digits being read off a screen and typed in, which is the exact
 * job tabular mono exists for. The step also gained the escape it was missing:
 * "wrong address? change it" returns to the email step instead of leaving a
 * user stuck waiting for mail that will never arrive. The attempts-remaining
 * counter now reads as a sentence rather than a bare number appended to an
 * error string.
 */
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@garden/ui";

import { ONBOARDING_NS, useEmailVerify } from "@/features/onboarding/api";
import { ApiError } from "@/lib/api-client";
import { STEP_H1_CLASS } from "@/features/onboarding/steps/shared";

const CODE_LENGTH = 6;
const RESEND_SECONDS = 42;
const INITIAL_ATTEMPTS = 3;

const EMPTY_CODE: readonly string[] = Array.from({ length: CODE_LENGTH }, () => "");

export interface OnbEmailOtpProps {
  /** Email the code was sent to (display only). */
  email: string;
  /** The new user's id — the verify payload. */
  userId: string;
  /**
   * NON-PROD only: the plaintext OTP echoed by the signup response when there
   * is no real email delivery (QA/local). Always `null`/absent in production.
   */
  devOtp?: string | null;
  /** Re-issue the email code (the flow re-calls signup). */
  onResend: () => void;
  /** Go back and correct the address (the flow returns to the email step). */
  onChangeEmail: () => void;
  /** Called with the returned access_token on a correct code. */
  onVerified: (accessToken: string) => void;
}

export function OnbEmailOtp({
  email,
  userId,
  devOtp,
  onResend,
  onChangeEmail,
  onVerified,
}: OnbEmailOtpProps): ReactElement {
  const { t } = useTranslation(ONBOARDING_NS);
  const [code, setCode] = useState<string[]>([...EMPTY_CODE]);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState(INITIAL_ATTEMPTS);
  const [count, setCount] = useState(RESEND_SECONDS);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const verifyMutation = useEmailVerify();

  // Resend countdown — ticks once a second down to zero, then stops.
  useEffect(() => {
    if (count <= 0) return;
    const id = window.setInterval(() => {
      setCount((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [count]);

  const focusInput = (index: number): void => {
    inputs.current[index]?.focus();
  };

  const verify = (value: string): void => {
    if (verifyMutation.isPending) return;
    verifyMutation.mutate(
      { user_id: userId, otp: value },
      {
        onSuccess: (session) => onVerified(session.access_token),
        onError: (error) => {
          const locked = error instanceof ApiError && error.status === 429;
          setErrorKey(locked ? "otp.errLock" : "otp.errWrong");
          setAttemptsLeft((n) => Math.max(0, n - 1));
          setCode([...EMPTY_CODE]);
          focusInput(0);
        },
      },
    );
  };

  const setDigit = (index: number, raw: string): void => {
    // Keep only the last typed digit (handles autofill / paste of one char).
    const digit = raw.replace(/\D/g, "").slice(-1);
    if (raw !== "" && digit === "") return; // ignore non-digit input
    const next = [...code];
    next[index] = digit;
    setCode(next);
    if (errorKey) setErrorKey(null);
    if (digit && index < CODE_LENGTH - 1) {
      focusInput(index + 1);
    }
    if (next.every((d) => d !== "")) {
      verify(next.join(""));
    }
  };

  const onKeyDown = (
    index: number,
    event: ReactKeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === "Backspace" && code[index] === "" && index > 0) {
      focusInput(index - 1);
    }
  };

  const resend = (): void => {
    setCount(RESEND_SECONDS);
    setErrorKey(null);
    setCode([...EMPTY_CODE]);
    setAttemptsLeft(INITIAL_ATTEMPTS);
    onResend();
    focusInput(0);
  };

  const hasError = errorKey !== null;
  const errorIsLock = errorKey === "otp.errLock";

  return (
    <div className="flex flex-1 flex-col" data-testid="onb-email-otp">
      <div className="flex-1">
        <h1 className={STEP_H1_CLASS}>{t("otp.h1")}</h1>
        <p className="mb-6 mt-2.5 text-[15px] leading-[1.5] text-[var(--g-ink-2)]">
          {t("otp.lede")}{" "}
          <b className="font-semibold text-foreground" data-testid="onb-email-otp-dest">
            {email}
          </b>
        </p>

        {devOtp ? (
          <div
            role="note"
            data-testid="onb-email-otp-dev-hint"
            className="mb-6 rounded-[var(--g-radius)] border border-[var(--g-rule)] bg-[var(--g-sunken)] px-3.5 py-3 text-[13px] leading-snug text-[var(--g-ink-2)]"
          >
            {t("otp.devHint")}{" "}
            <b className="g-data text-foreground tracking-[0.25em]">{devOtp}</b>
          </div>
        ) : null}

        <div className="grid grid-cols-6 gap-2" data-testid="onb-email-otp-inputs">
          {code.map((digit, index) => {
            const filled = digit !== "";
            const borderClass = hasError
              ? "border-[var(--g-error)]"
              : filled
                ? "border-primary shadow-[0_0_0_3px_var(--g-primary-soft)]"
                : "border-[var(--g-rule)]";
            return (
              <input
                // 6 fixed boxes — index key is correct and stable here.
                key={index}
                ref={(el) => {
                  inputs.current[index] = el;
                }}
                value={digit}
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                maxLength={1}
                aria-label={t("otp.inputAria", { position: index + 1 })}
                aria-invalid={hasError || undefined}
                disabled={verifyMutation.isPending}
                data-testid={`onb-email-otp-input-${index}`}
                onChange={(e) => setDigit(index, e.target.value)}
                onKeyDown={(e) => onKeyDown(index, e)}
                className={[
                  "g-data h-[58px] w-full min-w-0 rounded-[var(--g-radius)] border bg-[var(--g-surface)]",
                  "text-center text-[22px] font-bold text-[var(--g-figure)] outline-none",
                  "transition-[border-color,box-shadow] duration-150",
                  "focus-visible:border-primary focus-visible:shadow-[0_0_0_3px_var(--g-primary-soft)]",
                  "disabled:opacity-60",
                  borderClass,
                ].join(" ")}
              />
            );
          })}
        </div>

        {hasError && (
          <div
            role="alert"
            data-testid="onb-email-otp-error"
            className="mt-3 text-[13px] font-semibold leading-snug text-[var(--g-error)]"
          >
            {t(errorKey)}
            {!errorIsLock && (
              <>
                {" "}
                {/* `left`, not `count` — `count` would hand the string to
                    i18next's plural resolver and need four RU plural forms. */}
                <span>{t("otp.attemptsLeft", { left: attemptsLeft })}</span>
              </>
            )}
          </div>
        )}

        <div className="mt-[18px] flex flex-wrap items-center gap-x-4 gap-y-2 text-[13.5px] text-[var(--g-ink-2)]">
          {count > 0 ? (
            <span data-testid="onb-email-otp-countdown">
              {t("otp.resendIn")}{" "}
              <b className="g-data text-foreground">
                0:{String(count).padStart(2, "0")}
              </b>
            </span>
          ) : (
            <button
              type="button"
              data-testid="onb-email-otp-resend"
              onClick={resend}
              className="min-h-11 border-none bg-transparent text-[13.5px] font-bold text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-ground)]"
            >
              {t("otp.resend")}
            </button>
          )}
          {/* The missing escape: a typo in the address used to be a dead end. */}
          <button
            type="button"
            data-testid="onb-email-otp-change"
            onClick={onChangeEmail}
            className="min-h-11 border-none bg-transparent text-[13.5px] font-semibold text-[var(--g-ink-2)] underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-ground)]"
          >
            {t("otp.changeEmail")}
          </button>
        </div>
      </div>

      <div className="pt-6">
        <Button
          size="lg"
          full
          trailing="arrowR"
          loading={verifyMutation.isPending}
          disabled={code.some((d) => d === "")}
          data-testid="onb-email-otp-continue"
          onClick={() => verify(code.join(""))}
        >
          {t("cta.continue")}
        </Button>
      </div>
    </div>
  );
}

export default OnbEmailOtp;
